import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Obecné školení BOZP - V1 DRAFT. Authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs per the Czech BOZP research
 * (docs/specs/2026-06-23-bozp-research-czech-legal.md). Czech-only
 * (LocalizedText cs-CZ). 5 chapters across the text + interactive block catalog
 * and a graded quiz with one question per kind. Answer keys live here
 * (repo-privileged) and in the DB; stripped from the client payload by the API.
 */
export const generalBozpProfile: TrainingProfile = {
  slug: 'general-bozp',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column. Chapter titles below stay LocalizedText.
  title: 'Obecné školení BOZP',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-pravni-zaklad',
        title: t('Právní rámec BOZP', 'The OHS legal framework'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-pravni',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'Právní rámec bezpečnosti a ochrany zdraví při práci',
              'The legal framework for occupational health & safety',
            ),
          },
          {
            id: 'p-pravni-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Základní povinnosti v oblasti BOZP stanoví **zákoník práce (zákon č. 262/2006 Sb.), část pátá, §§ 101-108**. Zaměstnavatel je povinen zajistit bezpečnost a ochranu zdraví zaměstnanců s ohledem na rizika možného ohrožení jejich života a zdraví, která se týkají výkonu práce.',
              'The core occupational health & safety (OHS) duties are set out in the **Labour Code (Act No. 262/2006 Coll.), Part Five, §§ 101-108**. The employer must ensure the safety and health of employees with regard to the risks that may endanger their life and health in the course of their work.',
            ),
          },
          {
            id: 'c-pravni-naklady',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Podle **§ 101 zákoníku práce** nese náklady spojené se zajištěním BOZP zaměstnavatel; tyto náklady **nesmí být přenášeny** přímo ani nepřímo na zaměstnance.',
              'Under **§ 101 of the Labour Code**, the costs of ensuring OHS are borne by the employer; these costs **must not be transferred**, directly or indirectly, to employees.',
            ),
          },
          {
            id: 'p-pravni-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Podle **§ 103 odst. 2 a 3** zaměstnavatel zajišťuje školení o právních a ostatních předpisech k zajištění BOZP a sám určuje **obsah, četnost, způsob ověřování znalostí a vedení dokumentace**. Zákon nestanoví jediný pevný interval - cadence je rizikově podloženým rozhodnutím zaměstnavatele.',
              'Under **§ 103(2) and (3)**, the employer provides training on the legal and other regulations for ensuring OHS and itself determines the **content, frequency, method of knowledge verification and record-keeping**. The law sets no single fixed interval - the cadence is a risk-based decision of the employer.',
            ),
          },
          {
            id: 'k-pravni',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Páteří BOZP je **§ 103 zákoníku práce**: zaměstnavatel zajišťuje školení odpovídající rizikům práce a vede o něm dokumentaci. Náklady nese vždy **zaměstnavatel**.',
              'The backbone of OHS is **§ 103 of the Labour Code**: the employer provides training matched to the risks of the work and keeps records of it. The costs are always borne by the **employer**.',
            ),
          },
        ],
      },
      {
        id: 'ch-rizika',
        title: t('Rizika na pracovišti', 'Workplace risks'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-rizika',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Prevence rizik a rizikové faktory', 'Risk prevention and risk factors'),
          },
          {
            id: 'p-rizika-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Podle **§ 102 zákoníku práce** zaměstnavatel soustavně vyhledává, posuzuje a hodnotí rizika a přijímá opatření k jejich odstranění. Platí jasná hierarchie: **odstranit riziko u zdroje** má přednost před technickými opatřeními a **kolektivní ochrana má přednost před osobní**.',
              'Under **§ 102 of the Labour Code**, the employer continuously identifies, assesses and evaluates risks and adopts measures to eliminate them. A clear hierarchy applies: **eliminating the risk at source** takes precedence over technical measures, and **collective protection takes precedence over personal protection**.',
            ),
          },
          {
            id: 'cl-rizika',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Fyzikální faktory - hluk, vibrace, prach, osvětlení',
                'Physical factors - noise, vibration, dust, lighting',
              ),
              t(
                'Mechanická rizika - pád z výšky, zachycení, pořezání',
                'Mechanical risks - falls from height, entanglement, cuts',
              ),
              t(
                'Ergonomická zátěž - ruční manipulace s břemeny, nevhodné polohy',
                'Ergonomic strain - manual handling of loads, awkward postures',
              ),
              t(
                'Chemické a biologické faktory - nebezpečné látky, SDS',
                'Chemical and biological factors - hazardous substances, SDS',
              ),
            ],
          },
          {
            id: 'c-rizika-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Každé zjištěné riziko nahlas svému nadřízenému. Práci, která bezprostředně a závažně ohrožuje život nebo zdraví, máš podle **§ 106** právo odmítnout.',
              'Report every identified risk to your supervisor. Under **§ 106**, you have the right to refuse work that poses an immediate and serious threat to life or health.',
            ),
          },
          {
            id: 'acc-rizika',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t('Co znamená prevence rizik?', 'What does risk prevention mean?'),
                a: t(
                  'Soubor opatření vyplývajících z právních předpisů a z opatření zaměstnavatele, která mají za cíl předcházet rizikům, odstraňovat je nebo minimalizovat jejich působení.',
                  "A set of measures arising from legal regulations and from the employer's own measures, aimed at preventing risks, eliminating them, or minimising their effect.",
                ),
              },
              {
                q: t(
                  'Jaké je pořadí ochranných opatření?',
                  'What is the order of protective measures?',
                ),
                a: t(
                  'Nejprve odstranění rizika u zdroje, poté kolektivní (technická) ochrana a teprve nakonec osobní ochranné pracovní prostředky.',
                  'First eliminate the risk at source, then collective (technical) protection, and only as a last resort personal protective equipment.',
                ),
              },
            ],
          },
        ],
      },
      {
        id: 'ch-oopp',
        title: t('Osobní ochranné pracovní prostředky', 'Personal protective equipment'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-oopp'],
        blocks: [
          {
            id: 'h-oopp',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t(
              'OOPP - osobní ochranné pracovní prostředky',
              'PPE - personal protective equipment',
            ),
          },
          {
            id: 'p-oopp-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Pokud rizika nelze odstranit nebo dostatečně omezit kolektivní ochranou, poskytuje zaměstnavatel podle **§ 104 zákoníku práce** zaměstnancům **osobní ochranné pracovní prostředky (OOPP) bezplatně**. OOPP musí chránit před riziky, nesmí ohrožovat zdraví a musí odpovídat podmínkám na pracovišti.',
              'Where risks cannot be eliminated or sufficiently limited by collective protection, the employer provides employees, under **§ 104 of the Labour Code**, with **personal protective equipment (PPE) free of charge**. PPE must protect against the risks, must not endanger health, and must suit the conditions at the workplace.',
            ),
          },
          {
            id: 'anim-oopp',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'ppeMatch',
            steps: [
              {
                label: t('Pád předmětu → přilba', 'Falling object → helmet'),
                say: t(
                  'Kde se pracuje nad hlavou, vždy přilba.',
                  'Wherever work goes on overhead, a helmet.',
                ),
              },
              {
                label: t('Jiskry → brýle', 'Sparks → goggles'),
                say: t(
                  'Broušení a řezání: uzavřené ochranné brýle, ne jen dioptrické.',
                  'Grinding and cutting: sealed goggles, not just glasses.',
                ),
              },
              {
                label: t('Chemikálie → rukavice', 'Chemicals → gloves'),
                say: t(
                  'Rukavice podle bezpečnostního listu látky.',
                  'Gloves matched to the substance safety data sheet.',
                ),
              },
            ],
            caption: t(
              'Každé riziko má svůj ochranný prostředek - přilba, brýle, rukavice.',
              'Every risk has its protective item - helmet, goggles, gloves.',
            ),
          },
          {
            id: 'cl-oopp',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Ochrana hlavy - přilba na rizikových pracovištích',
                'Head protection - helmet at high-risk workplaces',
              ),
              t(
                'Ochrana zraku - brýle nebo štít při broušení a sekání',
                'Eye protection - goggles or a face shield when grinding and chipping',
              ),
              t(
                'Ochrana rukou - vhodné pracovní rukavice podle činnosti',
                'Hand protection - work gloves suited to the task',
              ),
              t(
                'Vizuální kontrola OOPP před každým použitím',
                'Visual inspection of PPE before every use',
              ),
            ],
          },
          {
            id: 'c-oopp-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              'Poškozené nebo prošlé OOPP **okamžitě vyřaď z používání** a vyžádej náhradu. Práce bez předepsaných OOPP je zakázána.',
              'Damaged or expired PPE **must be taken out of use immediately** and a replacement requested. Working without the prescribed PPE is prohibited.',
            ),
          },
          {
            id: 'kc-oopp',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Kdo hradí osobní ochranné pracovní prostředky?',
              'Who pays for personal protective equipment?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t('Zaměstnavatel - bezplatně', 'The employer - free of charge'),
              },
              {
                id: 'kc-b',
                label: t('Zaměstnanec ze své mzdy', 'The employee, out of their wages'),
              },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'Podle § 104 zákoníku práce poskytuje OOPP zaměstnavatel zaměstnancům bezplatně.',
              'Under § 104 of the Labour Code, the employer provides PPE to employees free of charge.',
            ),
          },
        ],
      },
      {
        id: 'ch-prvni-pomoc',
        title: t('První pomoc', 'First aid'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-prvni-pomoc'],
        blocks: [
          {
            id: 'h-prvni-pomoc',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Poskytnutí první pomoci', 'Giving first aid'),
          },
          {
            id: 'p-prvni-pomoc-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Při úrazu je prvořadé **zajistit vlastní bezpečnost i bezpečnost postiženého**, posoudit jeho stav (vědomí a dýchání) a neprodleně přivolat **zdravotnickou záchrannou službu na lince 155** (případně jednotné evropské číslo tísňového volání 112).',
              'In case of an injury, the first priority is to **secure your own safety and that of the casualty**, assess their condition (consciousness and breathing), and immediately call the **emergency medical service on 155** (or the single European emergency number 112).',
            ),
          },
          {
            id: 'ord-prvni-pomoc',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky první pomoci u osoby v bezvědomí do správného pořadí.',
              'Put the first-aid steps for an unconscious person into the correct order.',
            ),
            items: [
              {
                id: 'op-1',
                label: t(
                  'Zajisti bezpečí - sebe i postiženého',
                  "Secure safety - yours and the casualty's",
                ),
              },
              {
                id: 'op-2',
                label: t('Zkontroluj vědomí a dýchání', 'Check consciousness and breathing'),
              },
              {
                id: 'op-3',
                label: t(
                  'Přivolej záchrannou službu (155)',
                  'Call the emergency medical service (155)',
                ),
              },
              {
                id: 'op-4',
                label: t(
                  'Zahaj resuscitaci, pokud postižený nedýchá',
                  'Start resuscitation if the casualty is not breathing',
                ),
              },
            ],
          },
          {
            id: 'k-prvni-pomoc',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Pořadí je klíčové: **bezpečí → kontrola stavu → přivolání pomoci → resuscitace**. Linka ZZS: **155**, jednotné tísňové volání: **112**.',
              'The order is critical: **safety → check condition → call for help → resuscitation**. Emergency medical service: **155**, single emergency number: **112**.',
            ),
          },
        ],
      },
      {
        id: 'ch-mimoradne',
        title: t('Mimořádné události a evakuace', 'Emergencies and evacuation'),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-mimoradne',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Postup při mimořádné události', 'What to do in an emergency'),
          },
          {
            id: 'p-mimoradne-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Při požáru, úniku nebezpečné látky nebo jiné mimořádné události **zachovej klid, varuj ostatní a opusť ohrožený prostor** určenou únikovou cestou. Nikdy se nevracej pro osobní věci. Řiď se pokyny vedoucího evakuace a požárního poplachu.',
              'In a fire, a hazardous-substance leak, or another emergency, **stay calm, warn others, and leave the endangered area** by the designated escape route. Never go back for personal belongings. Follow the instructions of the evacuation leader and the fire alarm.',
            ),
          },
          {
            id: 'c-mimoradne-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Tísňové linky: **150** hasiči, **155** záchranná služba, **158** policie, **112** jednotné evropské číslo tísňového volání.',
              'Emergency numbers: **150** fire brigade, **155** medical rescue service, **158** police, **112** the single European emergency number.',
            ),
          },
          {
            id: 'cl-mimoradne',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Znej rozmístění únikových cest a východů na pracovišti',
                'Know the layout of escape routes and exits at your workplace',
              ),
              t(
                'Znej umístění hasicích přístrojů a lékárničky',
                'Know where the fire extinguishers and first-aid kit are',
              ),
              t(
                'Po opuštění budovy se shromáždi na určeném místě',
                'After leaving the building, gather at the designated assembly point',
              ),
            ],
          },
          {
            id: 'k-mimoradne',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Při mimořádné události je prioritou **ochrana života a zdraví**, nikoli majetku. Jednotné tísňové volání: **112**.',
              'In an emergency, the priority is **protecting life and health**, not property. The single emergency number: **112**.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-naklady-tf',
      kind: 'truefalse',
      chapterId: 'ch-pravni-zaklad',
      prompt: t(
        'Náklady spojené se zajištěním BOZP nese zaměstnavatel a nelze je přenášet na zaměstnance.',
        'The costs of ensuring OHS are borne by the employer and cannot be transferred to employees.',
      ),
      correct: true,
    },
    {
      id: 'q-rizika-multi',
      kind: 'multi',
      chapterId: 'ch-rizika',
      prompt: t(
        'Které z následujících patří mezi rizikové faktory na pracovišti? (vyber všechny správné)',
        'Which of the following are workplace risk factors? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Nadměrný hluk', 'Excessive noise') },
        { id: 'b', label: t('Pád z výšky', 'A fall from height') },
        {
          id: 'c',
          label: t(
            'Ergonomická zátěž při ruční manipulaci',
            'Ergonomic strain during manual handling',
          ),
        },
        { id: 'd', label: t('Barva stěn kanceláře', 'The colour of the office walls') },
      ],
      correct: ['a', 'b', 'c'],
    },
    {
      id: 'q-oopp-single',
      kind: 'single',
      chapterId: 'ch-oopp',
      prompt: t(
        'Který druh ochrany má podle hierarchie opatření přednost?',
        'Which type of protection takes precedence under the hierarchy of measures?',
      ),
      choices: [
        {
          id: 'a',
          label: t('Osobní ochranné pracovní prostředky', 'Personal protective equipment'),
        },
        {
          id: 'b',
          label: t('Kolektivní (technická) ochrana', 'Collective (technical) protection'),
        },
        { id: 'c', label: t('Bezpečnostní značení', 'Safety signage') },
      ],
      correct: 'b',
    },
    {
      id: 'q-zzs-numeric',
      kind: 'numeric',
      chapterId: 'ch-mimoradne',
      prompt: t(
        'Jaké je telefonní číslo zdravotnické záchranné služby v České republice?',
        'What is the phone number of the emergency medical service in the Czech Republic?',
      ),
      correct: 155,
      tolerance: 0,
    },
    {
      id: 'q-prvni-pomoc-order',
      kind: 'ordering',
      chapterId: 'ch-prvni-pomoc',
      prompt: t(
        'Seřaď kroky první pomoci u osoby v bezvědomí.',
        'Order the first-aid steps for an unconscious person.',
      ),
      choices: [
        { id: 'a', label: t('Zajisti bezpečí', 'Secure safety') },
        { id: 'b', label: t('Zkontroluj vědomí a dýchání', 'Check consciousness and breathing') },
        { id: 'c', label: t('Přivolej záchrannou službu', 'Call the emergency medical service') },
        { id: 'd', label: t('Zahaj resuscitaci', 'Start resuscitation') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
